import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import formRule from '@/libs/antd/rules';
import { FleetQueryParams } from '@/libs/fleet-api/fetcher';
import {
	getPipelineList,
	PipelineItems,
} from '@/libs/fleet-api/services/pipeline';
import { EditOutlined, LoadingOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
	Button,
	Col,
	DatePicker,
	Divider,
	Flex,
	Form,
	Input,
	Row,
	Select,
	Table,
} from 'antd';
import { FC, Fragment, useEffect, useState } from 'react';
import { isEditable } from '../utils/IsEditable';
import { getMasterData } from '@/libs/fleet-api/services/master-data';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { formatCurrency } from '@/libs/antd/formater';
import dayjs from 'dayjs';
import GenericErrorMessage from '@/components/GenericErrorMessage';

type FRMPipelineListPageNonJFProps = {
	prospectId: string;
};

const FRMPipelineListPageNonJF: FC<FRMPipelineListPageNonJFProps> = () => {
	const { navigate } = useApp();
	const { openCustomerDetail, openPipelineModal } = useModals();

	const searchForm = useForm<{
		by: 'period' | 'customer-name' | 'status';
		value: string;
	}>();

	const { data, isFetching, refetch, isError } = useQuery({
		queryKey: ['getPipeLineList'],
		queryFn: () => {
			const params: FleetQueryParams = {
				pagination: { pageSize: 20, pageNumber: 1 },
				filter: {
					by: 'plafon-type',
					value: 'NonJoinFinancing',
				},
				...(searchForm.value?.by || searchForm.value?.value
					? {
							search: {
								by: searchForm.value?.by ?? '',
								value: searchForm.value?.value ?? '',
							},
						}
					: {}),
			};
			return getPipelineList(params);
		},
	});

	const { data: prospectGroupStatus } = useQuery({
		queryKey: ['getMasterData'],
		queryFn: () => {
			return getMasterData('prospect-group-status');
		},
	});

	const [startDate, setStartDate] = useState<string>('');
	const [endDate, setEndDate] = useState<string>('');

	const updateDateValue = (newStartDate: string, newEndDate: string) => {
		const formattedValue = `${newStartDate}|${newEndDate}`;
		searchForm.form.setFieldsValue({ ['value']: formattedValue });
	};

	const [spreadData, setSpreadData] = useState<PipelineItems[]>([]);
	const dataSource = data?.data.items;
	useEffect(() => {
		if (dataSource) {
			const items = dataSource;
			const newPlafons: PipelineItems[] = [];
			items.forEach((item) => {
				const plafons =
					item.prospectInformation?.financingApplicationLimitInformation ?? [];
				if (plafons.length) {
					plafons.forEach((plafon) => {
						const spreadData = {
							napNo: item.napNo,
							pipelineNo: item.pipelineNo,
							pipelineHotProspectId: item.pipelineHotProspectId,
							prospectId: item.prospectId,
							customerName: item.customerName,
							customerId: item.customerId,
							customerGroup: item.customerGroup,
							existingFinancingLimit: plafon?.existingFinancingLimit,
							industrySector: item.industrySector,
							outstandingPrincipal: plafon?.outstandingPrincipal,
							limitApplication: plafon.limitApplication,
							financingPurpose: plafon.financingPurpose,
							plafonType: plafon.plafonType!,
							referrer: plafon.referrer,
							orderSource: plafon.orderSource,
							referrerUnitName: item.referrerUnitName,
							prospectStatus: item.prospectStatus,
							ultimateOwner: item.ultimateOwner,
							currentIssue: item.currentIssue,
							creditScheme: item.creditScheme,
							newOrderDate: item.newOrderDate,
							pipelineStatus: item.pipelineStatus,
							segmentMarket: plafon.segmentMarket,
							frmName: item.frmName,
							businessHeadName: item.businessHeadName,
						};
						newPlafons.push(spreadData);
					});
				}
			});
			setSpreadData(newPlafons);
		}
	}, [dataSource]);

	if (isError) return <GenericErrorMessage />;

	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<Divider />
			<FleetTitle>Pipeline Management</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				layout="horizontal"
				colon={false}
				labelAlign="left"
				onFinish={() => refetch()}
				form={searchForm.form}
			>
				<Row gutter={[20, 20]}>
					<Col flex="auto">
						<Form.Item
							label={'Search By'}
							labelCol={{ flex: 'auto' }}
							wrapperCol={{ style: { width: 250 } }}
							name={searchForm.fieldName('by')}
							rules={[formRule.required()]}
						>
							<Select
								placeholder="Search By"
								options={[
									{ label: 'Periode', value: 'period' },
									{ label: 'Nama Customer', value: 'customer-name' },
									{ label: 'Status Pengajuan', value: 'status' },
								]}
								onChange={(value) => {
									searchForm.form.setFieldsValue({ ['value']: '' });
									searchForm.form.setFieldsValue({ ['by']: value });
								}}
								style={{ display: 'block', width: '100%' }}
								onClear={async () => {
									await searchForm.form.resetFields();
									await refetch();
								}}
								allowClear
							/>
						</Form.Item>
					</Col>
					<Col flex="auto">
						{searchForm.value?.by === 'period' ? (
							<Flex gap={12}>
								<Form.Item
									name={searchForm.fieldName('value')}
									hidden
									noStyle
									rules={[formRule.required()]}
								/>
								<Form.Item
									name="startDate"
									rules={[formRule.required()]}
									style={{ flex: 1 }}
								>
									<DatePicker
										onChange={(date) => {
											const newStartDate = date.format('YYYY-MM-DD');
											setStartDate(newStartDate);
											updateDateValue(newStartDate, endDate);
										}}
									/>
								</Form.Item>
								<Form.Item
									name="endDate"
									rules={[formRule.required()]}
									style={{ flex: 1 }}
								>
									<DatePicker
										onChange={(date) => {
											const newEndDate = date.format('YYYY-MM-DD');
											setEndDate(newEndDate);
											updateDateValue(startDate, newEndDate);
										}}
									/>
								</Form.Item>
							</Flex>
						) : searchForm.value?.by === 'status' ? (
							<Form.Item
								name={searchForm.fieldName('value')}
								rules={[formRule.required()]}
							>
								<Select options={prospectGroupStatus?.data} />
							</Form.Item>
						) : (
							<Form.Item
								name={searchForm.fieldName('value')}
								rules={[formRule.required()]}
							>
								<Input
									placeholder="Search..."
									style={{ display: 'block' }}
								/>
							</Form.Item>
						)}
					</Col>
					<Col>
						<Form.Item>
							<Button
								type="primary"
								htmlType="submit"
							>
								SEARCH
							</Button>
						</Form.Item>
					</Col>
				</Row>
			</Form>

			<Divider />

			<Table
				scroll={{ x: 'max-content' }}
				bordered
				dataSource={spreadData}
				loading={isFetching}
				pagination={false}
				columns={[
					{
						width: 1,
						title: 'No',
						align: 'center',
						render: (_, _record, index) => index + 1,
					},
					{
						title: 'Pipeline ID',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openPipelineModal(record.pipelineHotProspectId!);
								}}
							>
								{record.pipelineNo}
							</a>
						),
					},
					{
						title: 'Nama Customer',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(record.customerId ?? '');
								}}
							>
								{record.customerName}
							</a>
						),
					},
					{
						title: 'Group Customer BMPP',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => record.customerGroup?.value ?? '-',
					},
					{
						title: 'Status',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) =>
							record.existingFinancingLimit > 0 ? 'Existing' : 'New',
					},
					{
						title: 'Kelas Industri',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => record.industrySector?.industryClass ?? '-',
					},
					{
						title: 'Portofolio Guideline',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						align: 'center',
						render: (_, record) =>
							record.industrySector?.portofolioGuideline ?? '-',
					},
					{
						title: 'Nama FRM',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => record.frmName,
					},

					{
						title: 'Nama FBDH',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => record.businessHeadName,
					},
					{
						title: 'Status Pengajuan',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						align: 'center',
						render: (_, record) => record.prospectStatus?.value ?? '-',
					},
					{
						title: 'New Order Date',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) =>
							dayjs(record.newOrderDate).format('DD-MM-YYYY'),
					},
					{
						title: 'Jenis Plafon',
						render: (_, record) => record.plafonType?.value ?? '-',
					},
					{
						title: 'Tujuan Pembiayaan',
						render: (_, record) => record.financingPurpose ?? '-',
					},
					{
						title: 'Outstanding Principal',
						align: 'right',
						render: (_, record) =>
							record.outstandingPrincipal
								? formatCurrency(record.outstandingPrincipal)
								: '-',
					},
					{
						title: 'Plafond Existing',
						align: 'right',
						render: (_, record) =>
							record.existingFinancingLimit
								? formatCurrency(record.existingFinancingLimit)
								: '-',
					},
					{
						title: 'Plafond Diajukan',
						align: 'right',
						render: (_, record) => formatCurrency(record.limitApplication),
					},
					{
						title: 'Sumber Order',
						render: (_, record) => record.orderSource?.value ?? '-',
					},
					{
						title: 'Detail Sumber Order',
						render: (_, record) => record.referrer?.unitName ?? '-',
					},
					{
						title: 'Ultimate Owner',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => record.ultimateOwner ?? '-',
					},
					{
						title: 'Current Issue',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => (
							<span style={{ whiteSpace: 'pre-wrap' }}>
								{record.currentIssue ?? '-'}
							</span>
						),
					},
					{
						title: 'Skema Kredit',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						render: (_, record) => (
							<span style={{ whiteSpace: 'pre-wrap' }}>
								{record.creditScheme ?? '-'}
							</span>
						),
					},

					{
						title: 'Edit',
						width: 1,
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						align: 'center',
						render: (_, record) => (
							<a
								style={{
									display: 'block',
									...(isEditable(record.pipelineStatus.id)
										? {}
										: { color: 'grey', cursor: 'default' }),
								}}
								onClick={(e) => {
									e.preventDefault();
									if (!isEditable(record.pipelineStatus.id)) return;
									navigate('FRMPipelinePage', {
										prospectId: record.prospectId,
									});
								}}
							>
								<EditOutlined />
							</a>
						),
					},
					{
						title: 'Status Pipeline',
						onCell: (record, index?: number) => {
							if (record.prospectId === spreadData[index! + 1]?.prospectId) {
								return { rowSpan: 2 };
							}
							if (record.prospectId === spreadData[index! - 1]?.prospectId) {
								return { rowSpan: 0 };
							}

							return {};
						},
						align: 'center',
						render: (_, record) => record.pipelineStatus?.value ?? '-',
					},
				]}
			/>
		</Fragment>
	);
};

export default FRMPipelineListPageNonJF;
